import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Chip, CircularProgress, Alert } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { projectService, farmOperationService } from '@smartgrow/shared';

// Vite bundles Leaflet's default marker image paths incorrectly out of the box —
// re-point them at the bundled asset URLs so pins actually render.
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const NIGERIA_CENTER: [number, number] = [9.082, 8.6753];

export const MapPage = () => {
  const navigate = useNavigate();
  const { data: projectsData, isLoading: projectsLoading, isError: projectsError } = useQuery({
    queryKey: ['projects-all-map'],
    queryFn: () => projectService.getProjects({ size: 200 }),
  });

  const { data: operationsData, isLoading: operationsLoading, isError: operationsError } = useQuery({
    queryKey: ['farm-operations-all-map'],
    queryFn: () => farmOperationService.getAll(0, 200),
  });

  const projectPins = useMemo(
    () => (projectsData?.content ?? []).filter((p) => p.latitude != null && p.longitude != null),
    [projectsData],
  );
  const operationPins = useMemo(
    () => (operationsData?.content ?? []).filter((o) => o.latitude != null && o.longitude != null),
    [operationsData],
  );

  const isLoading = projectsLoading || operationsLoading;
  const isError = projectsError || operationsError;
  const totalPins = projectPins.length + operationPins.length;

  const mapCenter: [number, number] = totalPins > 0
    ? projectPins[0]
      ? [Number(projectPins[0].latitude), Number(projectPins[0].longitude)]
      : [Number(operationPins[0].latitude), Number(operationPins[0].longitude)]
    : NIGERIA_CENTER;

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>Farm Map</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Pins are set manually — add latitude/longitude on a Project or Farm Operation's edit page to show it here.
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : isError ? (
        <Alert severity="error">Failed to load map data.</Alert>
      ) : totalPins === 0 ? (
        <Alert severity="info">
          No locations pinned yet. Open a Project or Farm Operation's Edit page and enter Latitude/Longitude to plot it here.
        </Alert>
      ) : null}

      <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 2 }}>
        <MapContainer center={mapCenter} zoom={totalPins > 0 ? 8 : 6} style={{ height: 560, width: '100%' }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          {projectPins.map((p) => (
            <Marker key={`project-${p.id}`} position={[Number(p.latitude), Number(p.longitude)]}>
              <Popup>
                <Chip label="Project" size="small" color="primary" sx={{ mb: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" color="primary"
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => navigate(`/dashboard/projects/${p.id}`)}
                >
                  {p.projectName}
                </Typography>
                {p.location && <Typography variant="caption" color="text.secondary">{p.location}</Typography>}
              </Popup>
            </Marker>
          ))}
          {operationPins.map((o) => (
            <Marker key={`operation-${o.id}`} position={[Number(o.latitude), Number(o.longitude)]}>
              <Popup>
                <Chip label="Farm Operation" size="small" color="secondary" sx={{ mb: 1 }} />
                <Typography variant="subtitle2" fontWeight="bold" color="primary"
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => navigate(`/dashboard/farm/${o.id}`)}
                >
                  {o.name}
                </Typography>
                <Typography variant="caption" color="text.secondary"
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  onClick={() => navigate(`/dashboard/projects/${o.projectId}`)}
                >
                  {o.projectName}
                </Typography>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </Paper>
    </Box>
  );
};
